import type { Nhom, PartnerCard } from '@qlk/shared';
import { PHAM_VI_NHAN_CO_DINH } from '@qlk/shared';
import { Filter } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/**
 * Hàng chip phạm vi của trang Hiệu suất.
 *
 * Hai chip cố định (`Tất cả kênh`, `Kênh tôi quản lý`) cộng một nút mở menu
 * chọn **một** Network hoặc **một** nhóm. Phạm vi nằm trong `?pham-vi=` nên
 * dán link ra là người khác mở đúng phạm vi đó.
 *
 * Chip "Kênh tôi quản lý" **chỉ hiện khi có kênh để xem**: quản trị không phụ
 * trách kênh nào mà bấm vào đó thì cả trang trống, và không ô nào nói vì sao.
 *
 * Danh sách nhóm lấy từ endpoint danh sách nhóm của module 10
 * (`GET /team/nhom`), **không** gom từ enricher `chiaSe` của bảng kênh:
 * enricher đó mang dữ liệu chia tiền, dựng menu bằng nó là lấy một con số kế
 * toán ra làm nhãn giao diện. Thiếu `team:view` thì không có mục nhóm — đúng,
 * vì người đó cũng không được xem nhóm ở chỗ khác.
 */

export interface ChipPhamViProps {
  giaTri: string;
  onDoi: (giaTri: string) => void;
  /** Network đang trả tiền, từ module 02. */
  network: PartnerCard[];
  /** Nhóm nhân sự, từ module 10. */
  nhom: Nhom[];
  /** `false` ⇒ ẩn chip "Kênh tôi quản lý" (người này không phụ trách kênh nào). */
  coKenhToiQuanLy: boolean;
}

export function ChipPhamVi({ giaTri, onDoi, network, nhom, coKenhToiQuanLy }: ChipPhamViProps) {
  const [moMenu, datMoMenu] = useState(false);
  const boc = useRef<HTMLDivElement>(null);

  /* Bấm ra ngoài thì đóng menu — không dùng hộp thoại cho một menu lọc. */
  useEffect(() => {
    if (!moMenu) return;
    const ngoai = (e: MouseEvent) => {
      if (boc.current && !boc.current.contains(e.target as Node)) datMoMenu(false);
    };
    document.addEventListener('mousedown', ngoai);
    return () => {
      document.removeEventListener('mousedown', ngoai);
    };
  }, [moMenu]);

  const dangLoc = giaTri.startsWith('network:') || giaTri.startsWith('nhom:');
  const tenDangLoc = dangLoc
    ? giaTri.startsWith('network:')
      ? (network.find((n) => n.id === giaTri.slice(8))?.name ?? 'Network')
      : (nhom.find((n) => n.id === giaTri.slice(5))?.name ?? 'Nhóm')
    : null;

  const chon = (ma: string) => {
    onDoi(ma);
    datMoMenu(false);
  };

  return (
    <div className="mb-6 flex flex-wrap items-center gap-2">
      <Chip
        nhan={PHAM_VI_NHAN_CO_DINH['tat-ca']}
        chon={giaTri === 'tat-ca'}
        onBam={() => chon('tat-ca')}
      />

      {coKenhToiQuanLy && (
        <Chip
          nhan={PHAM_VI_NHAN_CO_DINH['toi-quan-ly']}
          chon={giaTri === 'toi-quan-ly'}
          onBam={() => chon('toi-quan-ly')}
        />
      )}

      <div className="relative" ref={boc}>
        <button
          type="button"
          aria-expanded={moMenu}
          aria-haspopup="menu"
          onClick={() => {
            datMoMenu((x) => !x);
          }}
          className={`vung-bam inline-flex items-center gap-1.5 rounded-chip border px-4 text-co-ban transition-colors ${
            dangLoc
              ? 'border-nhan bg-nhan-nen font-semibold text-nhan'
              : 'border-vien bg-be-mat text-chu-phu hover:text-chu'
          }`}
        >
          <Filter size={16} aria-hidden="true" />
          {tenDangLoc ?? 'Lọc theo Network / nhóm'}
        </button>

        {moMenu && (
          <div
            role="menu"
            className="absolute left-0 top-full z-20 mt-1 max-h-[320px] w-[260px] overflow-y-auto rounded-the border border-vien bg-be-mat p-1 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
          >
            {network.length === 0 && nhom.length === 0 && (
              <p className="px-3 py-2 text-phu text-chu-mo">Chưa có Network hay nhóm nào</p>
            )}

            {network.length > 0 && <NhanNhom nhan="Network" />}
            {network.map((n) => (
              <MucMenu
                key={`network-${n.id}`}
                nhan={n.name}
                chon={giaTri === `network:${n.id}`}
                onBam={() => chon(`network:${n.id}`)}
              />
            ))}

            {nhom.length > 0 && <NhanNhom nhan="Nhóm nhân sự" />}
            {nhom.map((n) => (
              <MucMenu
                key={`nhom-${n.id}`}
                nhan={n.name}
                chon={giaTri === `nhom:${n.id}`}
                onBam={() => chon(`nhom:${n.id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ nhan, chon, onBam }: { nhan: string; chon: boolean; onBam: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={chon}
      onClick={onBam}
      className={`vung-bam inline-flex items-center rounded-chip border px-4 text-co-ban transition-colors ${
        chon
          ? 'border-nhan bg-nhan-nen font-semibold text-nhan'
          : 'border-vien bg-be-mat text-chu-phu hover:text-chu'
      }`}
    >
      {nhan}
    </button>
  );
}

function NhanNhom({ nhan }: { nhan: string }) {
  return <p className="px-3 pb-1 pt-2 text-phu uppercase tracking-wide text-chu-mo">{nhan}</p>;
}

function MucMenu({ nhan, chon, onBam }: { nhan: string; chon: boolean; onBam: () => void }) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={chon}
      onClick={onBam}
      className={`vung-bam flex w-full items-center rounded-o px-3 text-left text-co-ban ${
        chon ? 'bg-nhan-nen font-semibold text-nhan' : 'text-chu hover:bg-neutral-nen'
      }`}
    >
      <span className="truncate">{nhan}</span>
    </button>
  );
}
